import type { FC, PropsWithChildren } from "react";
import React, { useCallback, useState } from "react";

import Button from "@busbud/design-system-components/dist/Button";
import Collapse from "@busbud/design-system-components/dist/Collapse";
import CaretDownSM from "@busbud/design-system-components/dist/Icons/CaretDown/SM";
import CaretUpSM from "@busbud/design-system-components/dist/Icons/CaretUp/SM";
import EllipsisVerticalSM from "@busbud/design-system-components/dist/Icons/EllipsisVertical/SM";

import { useAppContext } from "@app/helpers/hooks";

import { useStyles } from "./intermediate-stops-collapse.styles";
import { formatDepartureDuration } from "../../../../helpers/departure-helpers";
import {
  getDuration,
  getFormattedDuration
} from "../../../../helpers/duration";
import { CheckpointLayout } from "../../checkpoint-layout";
import { CheckpointPath } from "../../checkpoint-path";

interface Props {
  arrival_time: string | null;
  departure_time: string | null;
  number_of_stops: number;
  onCollapse?: () => void;
  onExpand?: () => void;
}

export const IntermediateStopsCollapse: FC<PropsWithChildren<Props>> = ({
  arrival_time,
  children,
  departure_time,
  number_of_stops,
  onCollapse,
  onExpand
}) => {
  const { classes } = useStyles();
  const { translator } = useAppContext();

  const [is_expanded, setExpanded] = useState(false);

  const toggleContent = useCallback(() => {
    setExpanded(!is_expanded);

    if (is_expanded && onCollapse) {
      onCollapse();
    }

    if (!is_expanded && onExpand) {
      onExpand();
    }
  }, [onCollapse, onExpand, is_expanded]);

  const duration = getDuration(departure_time, arrival_time);

  const formatted_duration =
    duration.days() === 0 && duration.hours() === 0
      ? getFormattedDuration(duration, translator)
      : formatDepartureDuration(duration);

  const Icon = is_expanded ? CaretUpSM : CaretDownSM;

  const stops_copy =
    number_of_stops === 1
      ? translator.t("!itinerary.stops.singular")
      : translator.t("!itinerary.stops.plural", {
          stops_count: number_of_stops
        });

  const button_text = is_expanded
    ? translator.t("!itinerary.stops.hide-stops")
    : `${formatted_duration}, ${stops_copy}`;

  return (
    <>
      <CheckpointLayout
        slot_middle={
          <CheckpointPath
            icon={<EllipsisVerticalSM color="icon.primary.opaque" />}
          />
        }
        slot_end={
          <>
            <Button
              className={classes.button}
              color="secondary"
              variant="text"
              endIcon={<Icon color="icon.primary.opaque" />}
              onClick={toggleContent}
              aria-expanded={is_expanded}
            >
              {button_text}
            </Button>
          </>
        }
      />
      <Collapse in={is_expanded} aria-hidden={!is_expanded}>
        {children}
      </Collapse>
    </>
  );
};
